import React, {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
  useContext,
  useCallback,
} from "react";
import classNames from "classnames";
import {
  OrganisationContext,
  UserContext,
} from "../../../../layouts/MainLayout";
import { getAllDeviceList } from "../../../../services/DeviceService";
import Icons from "../../icons";
import "./deviceSearchBox.scss";
import { getUsersForTenant } from "../../../../services/UserService";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "../loadingSpinner/LoadingSpinner";

const debounceTime = 2000;
let timeoutCall = null;
const SearchBox = (
  {
    autoid,
    options,
    getOptionCallBack,
    isMultiSelect = false,
    isUserSearch = false,
    isFilter = false,
    placeholderText = "",
    titleText = "",
    isSpecificFilter = "",
    defaultSelectedVal = null,
    callFrom = "",
  },
  ref
) => {
  const { siteData } = useContext(OrganisationContext);
  const [searchText, setSearchText] = useState("");
  const [showDropDown, setShowDropDown] = useState(false);
  const [isSearchEnable, setIsSearchEnable] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [deviceList, setDeviceList] = useState([]);
  const [filteredDeviceList, setFilteredDeviceList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [filteredUserList, setFilteredUserList] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const dropdownRef = useRef();
  useImperativeHandle(ref, () => ({
    getIsSearchEnable: () => isSearchEnable,
    getSearchText: () => searchText,
    getSelectedList: () => selectedItems,
  }));

  const { userData, userCognitoData } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation("searchDevice");
  const isFromServiceTool = callFrom === "service-tool";

  const filterDeviceList = (list) => {
    const newList = [...list];
    if (newList.length) {
      selectedItems.forEach((elem) => {
        const index = newList.findIndex(
          (item) => item.DeviceName === elem.DeviceName
        );
        if (index > -1) {
          newList.splice(index, 1);
        }
      });
    }
    setFilteredDeviceList(newList);
  };

  const filterUserList = useCallback(
    (list) => {
      const newList = [...list];
      if (newList.length) {
        selectedItems.forEach((elem) => {
          const index = newList.findIndex((item) => item.Email === elem.Email);
          if (index > -1) {
            newList.splice(index, 1);
          }
        });
      }
    setFilteredUserList(newList);
    },
    [selectedItems]
  );

  const getDeviceList = async () => {
    setIsLoading(true);
    try {
      const tenantId = siteData.id;
      if (tenantId) {
        let queryParams = {
          tenantId: tenantId,
          sort: [{ DeviceName: "asc" }],
          searchDevices: {
            searchOnFields: ["DeviceName", "deviceUUIDFromTelemetry"],
            searchText: searchText,
          },
          projectionFields: ["DeviceName", "deviceUUIDFromTelemetry", "PK"],
        };

        if (isFromServiceTool) {
          delete queryParams.projectionFields;
          queryParams.serviceStatus = true
        }

        const response = await getAllDeviceList(queryParams);

        if (response?.data?.Items) {
          const deviceList = response?.data?.Items;
          setDeviceList(deviceList);
          filterDeviceList(deviceList);
          if (defaultSelectedVal) {
            const defaultVal = deviceList.filter((device) => {
              return device?.PK === defaultSelectedVal;
            });
            defaultVal.length && setSelectedItems([defaultVal[0]]);
          }
        } else {
          setDeviceList([]);
        }
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      // console.log("error => ", error);
    }
  };

  const getUserList = useCallback(async () => {
    setIsLoading(true);
    try {
      const queryParams = {
        tenantId: userData?.OrganizationID,
        requestedByUserId:
          userCognitoData?.signInUserSession?.accessToken?.payload?.username,
        sort: [{ email: "asc" }],
      };

      if (userData.UserRole !== "Super Admin") {
        queryParams.siteId = siteData.id;
      }

      if (searchText.length > 0) {
        queryParams.search = searchText;
      }

      const response = await getUsersForTenant(queryParams);

      if (response?.data?.Items) {
        setUserList(response?.data?.Items);
        filterUserList(response?.data?.Items);
      } else {
        setUserList([]);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  }, [filterUserList, searchText, userCognitoData, userData]);

  const handleKeyDown = (e) => {
    if (e.keyCode === 13) {
      setIsSearchEnable(false);
      isUserSearch ? getUserList() : getDeviceList();
    }
  };
  const handleOnChange = (e) => {
    setSearchText(e.target.value);
    setIsSearchEnable(e.target.value.length > 0);
  };
  const handleOptionSelect = (e, item) => {
    e.stopPropagation();
    let itemIndex;
    if (isMultiSelect) {
      let list = [...selectedItems];

      if (isUserSearch) {
        itemIndex = list.findIndex((elem) => elem.Email === item.Email);
      } else {
        itemIndex = list.findIndex(
          (elem) => elem.DeviceName === item.DeviceName
        );
      }

      if (itemIndex > -1) {
        list.splice(itemIndex, 1);
      } else {
        list.push(item);
      }
      setSelectedItems(list);
      // setShowDropDown(false);
      if (isFromServiceTool) {
        setSearchText("");
      }
    } else {
      setSelectedItems([item]);
      // setShowDropDown(false);
      if (isFromServiceTool) {
        setSearchText("");
      }
    }
    setIsSearchEnable(false);
  };
  const handleClearBtn = () => {
    setSearchText("");
    setIsSearchEnable(false);
  };

  const handleOutsideClick = (e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      // getOptionCallBack();
      setShowDropDown(false);
      setSearchText("");
      getUserList();
      if (isFromServiceTool) {
        setSearchText("");
      }
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  });

  useEffect(() => {
    if (siteData?.id) {
      setSelectedItems([]);
      isUserSearch ? getUserList() : getDeviceList();
      setIsLoaded(true)
    }
  }, [siteData]);

  useEffect(() => {
    if(isLoaded){
      if (timeoutCall !== null) {
        clearTimeout(timeoutCall);
      }
      timeoutCall = setTimeout(() => {
        isUserSearch ? getUserList() : getDeviceList();
      }, debounceTime);
    }
  }, [searchText]);

  useEffect(() => {
    isUserSearch ? filterUserList(userList) : filterDeviceList(deviceList);

    getOptionCallBack(selectedItems);
  }, [selectedItems]);

  const handleDropdownClick = useCallback(() => {
    setShowDropDown((prevVal) => !prevVal);
  }, []);

  const toolTip = useCallback(
    (item) => {
      if (item && isFromServiceTool) {
        return `${item?.DeviceName}${
          item?.deviceUUIDFromTelemetry
            ? `(${item?.deviceUUIDFromTelemetry})`
            : ""
        }`;
      } else {
        return "";
      }
    },
    [isFromServiceTool]
  );

  return (
    <div
      className={
        isSpecificFilter === "deviceFilter"
          ? classNames("filterBox deviceFilter", {
              active: showDropDown,
            })
          : isFilter
          ? classNames("filterBox", {
              active: showDropDown,
            })
          : classNames("deviceSearchBox", {
              active: showDropDown,
            })
      }
      ref={dropdownRef}
    >
      <div
        className="searchPlaceholder"
        onClick={handleDropdownClick}
        title={toolTip(selectedItems[0])}
      >
        {selectedItems.length ? (
          <div className="d-flex flex-fill align-items-center">
            <div
              className={classNames("selectedItems", {
                active: selectedItems.length > 1,
              })}
            >
              {isUserSearch ? (
                <>{selectedItems[0].Email}</>
              ) : (
                <>
                  {Icons.routerIcon}
                  {selectedItems[0].DeviceName}
                </>
              )}
            </div>
            {selectedItems.length > 1 && (
              <div className="itemsCount">{`+${selectedItems.length - 1}`}</div>
            )}
          </div>
        ) : (
          <>
            <span className="title-text">{titleText}</span>
          </>
        )}
        {Icons.chevronDownArrowIcon}
      </div>
      {showDropDown && (
        <div className="searchDropDownWrapper">
          
          {/* Loader  starts*/}
          {isLoading && <LoadingSpinner />}
          {/* Loader  Ends */}
          
          <input
            type="text"
            placeholder={placeholderText}
            name={autoid}
            id={autoid}
            onKeyDown={handleKeyDown}
            onChange={handleOnChange}
            value={searchText}
            autoFocus
          />
          {searchText?.length > 0 ? (
            <button
              type="button"
              id={`${autoid}_clear_btn`}
              onClick={handleClearBtn}
            >
              {Icons.closeIcon}
            </button>
          ) : (
            <button
              type="button"
              id={`${autoid}_search_btn`}
              className="searchBtn"
            >
              {Icons.searchIcon}
            </button>
          )}
          <ul className="searchDropDown">
            {selectedItems?.length > 0 && (
              (
                filteredUserList.length > 0 ||
                filteredDeviceList.length > 0 
              ) &&
              selectedItems.map((item, index) => (
                <li
                  onClick={(e) => handleOptionSelect(e, item)}
                  key={`${autoid}_option_selected_${index + 1}`}
                  className={
                    isUserSearch
                      ? classNames({
                          active: selectedItems.find(
                            (elem) => elem.Email === item?.Email
                          ),
                          hide: searchText && !((item?.Email).toLowerCase().includes(searchText)), 
                        })
                      : classNames({
                          active: selectedItems.find(
                            (elem) => elem.DeviceName === item?.DeviceName
                          ),
                          hide:
                            searchText && !((item?.DeviceName).toLowerCase().includes(searchText)), 
                        })
                  }
                  title={toolTip(item)}
                >
                  {isUserSearch ? (
                    <>
                      <div className="d-flex flex-column">
                        <span id={`label_${index}`} title={item?.Email}>
                          {item?.Email}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      {Icons.routerIcon}
                      <div className="d-flex flex-column">
                        <span id={`label_${index}`}>{item?.DeviceName}</span>
                        {item?.deviceUUIDFromTelemetry && (
                          <span className="deviceUID">
                            {item?.deviceUUIDFromTelemetry}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </li>
              )))}

            {selectedItems.length > 0 &&
              (filteredDeviceList.length > 0 ||
                filteredUserList.length > 0) && <li className="divider"></li>}

            {isUserSearch ? (
              <>
                {filteredUserList.length > 0 &&
                  filteredUserList.map((item, index) => (
                    <li
                      onClick={(e) => handleOptionSelect(e, item)}
                      key={`${autoid}_option_${index + 1}`}
                      className={classNames({
                        active: selectedItems.find(
                          (elem) => elem.Email === item?.Email
                        ),
                      })}
                    >
                      <div className="d-flex flex-column">
                        <span id={`label_${index}`} title={item?.Email}>
                          {item?.Email}
                        </span>
                      </div>
                    </li>
                  ))}
              </>
            ) : (
              <>
                {filteredDeviceList.length > 0 &&
                  filteredDeviceList.map((item, index) => (
                    <li
                      title={toolTip(item)}
                      onClick={(e) => handleOptionSelect(e, item)}
                      key={`${autoid}_option_${index + 1}`}
                      className={classNames({
                        active: selectedItems.find(
                          (elem) => elem.DeviceName === item?.DeviceName
                        ),
                      })}
                    >
                      {Icons.routerIcon}
                      <div className="d-flex flex-column">
                        <span id={`label_${index}`}>{item?.DeviceName}</span>
                        {item?.deviceUUIDFromTelemetry && (
                          <span className="deviceUID">
                            {item?.deviceUUIDFromTelemetry}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
              </>
            )}

            {isUserSearch ? (
              <>
              {!filteredUserList.length && <li>{t("no_result")}</li>}
              </>
            ) : (
              <>{!filteredDeviceList.length && <li>{t("no_result")}</li>}</>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default forwardRef(SearchBox);
